import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Shield, Wifi, Package, ArrowRight, ArrowLeft,
  CheckCircle2, Phone, Home, Building2, Factory, MapPin,
  User, ChevronRight, Sparkles, Star, Info, Monitor
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import StickyBreadcrumb from '../components/StickyBreadcrumb';

/* ═══════════ SEO — injeta/remove dinamicamente ════════════ */
function useSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Orçamento Intelbras Ribeirão Preto | Câmeras, Alarme e Wi-Fi | Fernando Tech';

    const setMeta = (attr, key, val) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('data-orcamento', '1');
      el.content = val;
    };

    setMeta('name', 'description',
      'Orçamento online gratuito de câmeras de segurança CFTV, sistemas de alarme e redes Wi-Fi Intelbras em Ribeirão Preto. Estimativa instantânea e sem compromisso. Fernando Tech — Parceiro Autorizado Intelbras.');
    setMeta('name', 'robots', 'index, follow');
    setMeta('property', 'og:title', 'Orçamento Intelbras | Fernando Tech');
    setMeta('property', 'og:description', 'Monte seu kit de segurança e receba estimativa de preço na hora. Câmeras CFTV, alarmes e Wi-Fi Intelbras.');
    setMeta('property', 'og:url', 'https://fernandotech.com.br/orcamento-intelbras');
    setMeta('property', 'og:type', 'website');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://fernandotech.com.br/orcamento-intelbras';
    canonical.setAttribute('data-orcamento', '1');

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': 'https://fernandotech.com.br/orcamento-intelbras',
          url: 'https://fernandotech.com.br/orcamento-intelbras',
          name: 'Orçamento Intelbras Ribeirão Preto | Câmeras, Alarme e Wi-Fi',
          description: 'Orçamento online gratuito de câmeras CFTV, sistemas de alarme e redes Wi-Fi Intelbras em Ribeirão Preto.',
          isPartOf: { '@id': 'https://fernandotech.com.br/#website' },
          breadcrumb: { '@id': 'https://fernandotech.com.br/orcamento-intelbras#breadcrumb' },
        },
        {
          '@type': 'BreadcrumbList',
          '@id': 'https://fernandotech.com.br/orcamento-intelbras#breadcrumb',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fernandotech.com.br/' },
            { '@type': 'ListItem', position: 2, name: 'Intelbras', item: 'https://fernandotech.com.br/#intelbras' },
            { '@type': 'ListItem', position: 3, name: 'Orçamento Intelbras', item: 'https://fernandotech.com.br/orcamento-intelbras' },
          ],
        },
        {
          '@type': 'Service',
          '@id': 'https://fernandotech.com.br/orcamento-intelbras#service',
          name: 'Instalação de Câmeras de Segurança, Alarmes e Wi-Fi Intelbras',
          description: 'Instalação profissional de câmeras CFTV, sistemas de alarme e redes Wi-Fi Intelbras para empresas e residências em Ribeirão Preto e região.',
          provider: { '@type': 'LocalBusiness', name: 'Fernando Tech', url: 'https://fernandotech.com.br' },
          areaServed: { '@type': 'City', name: 'Ribeirão Preto' },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '700',
            highPrice: '9000',
            priceCurrency: 'BRL',
          },
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'orcamento-intelbras-schema';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      document.querySelectorAll('[data-orcamento="1"]').forEach(el => el.remove());
      const s = document.getElementById('orcamento-intelbras-schema');
      if (s) s.remove();
    };
  }, []);
}

/* ═══════════ Dados (fallback — sobrescritos pela API) ═══════════════════ */
const DEFAULT_CAMERA_QTY = [
  { id: '2',  label: '2 câmeras',   sub: 'Residência pequena',       priceMin: 900,  priceMax: 1400 },
  { id: '4',  label: '4 câmeras',   sub: 'Casa / Comércio pequeno',  priceMin: 1500, priceMax: 2300 },
  { id: '8',  label: '8 câmeras',   sub: 'Comércio médio',           priceMin: 2800, priceMax: 4200 },
  { id: '12', label: '12 câmeras',  sub: 'Comércio / Condomínio',    priceMin: 4000, priceMax: 6500 },
  { id: '16', label: '16+ câmeras', sub: 'Grande porte / Indústria', priceMin: 5500, priceMax: 9000 },
];

const DEFAULT_ALARM_ZONES = [
  { id: '4',  label: '4 zonas',   sub: 'Residência / sala',     priceMin: 700,  priceMax: 1100 },
  { id: '8',  label: '8 zonas',   sub: 'Casa / comércio médio', priceMin: 1100, priceMax: 1800 },
  { id: '12', label: '12 zonas',  sub: 'Comércio / escritório', priceMin: 1700, priceMax: 2600 },
  { id: '16', label: '16+ zonas', sub: 'Grande porte',          priceMin: 2500, priceMax: 4000 },
];

const DEFAULT_WIFI_COVERAGE = [
  { id: 'ate100',  label: 'Até 100 m²',      sub: '1–2 ambientes',        aps: 1,  priceMin: 600,  priceMax: 1000 },
  { id: 'ate300',  label: 'Até 300 m²',      sub: 'Casa / escritório',    aps: 3,  priceMin: 1200, priceMax: 2200 },
  { id: 'ate800',  label: 'Até 800 m²',      sub: 'Comércio / condomínio',aps: 6,  priceMin: 2500, priceMax: 4500 },
  { id: 'grande',  label: 'Acima de 800 m²', sub: 'Indústria / galpão',   aps: 10, priceMin: 4500, priceMax: 8000 },
];

/* ═══════════ Dados estáticos (não mudam via API) ═══════════════════════ */
const SERVICES = [
  { id: 'cameras', label: 'Câmeras CFTV', icon: Camera, desc: 'Câmeras dome e bullet com acesso remoto' },
  { id: 'alarme',  label: 'Sistema de Alarme', icon: Shield, desc: 'Central com sensores e monitoramento 24h' },
  { id: 'wifi',    label: 'Rede Wi-Fi',  icon: Wifi,   desc: 'Access points Intelbras corporativos' },
  { id: 'combo',   label: 'Combo Completo', icon: Package, desc: 'Câmeras + Alarme + Wi-Fi · 10% de desconto', badge: 'Mais popular' },
];

const CAMERA_RES = [
  { id: 'hd',     label: 'HD',      detail: '720p',  sub: 'Custo-benefício' },
  { id: 'fullhd', label: 'Full HD', detail: '1080p', sub: 'Recomendado' },
  { id: '4k',     label: '4K',      detail: 'Ultra', sub: 'Máx. qualidade', extra: 800 },
];

const LOCATION_TYPES = [
  { id: 'residencia', label: 'Residência', icon: Home },
  { id: 'comercio',   label: 'Comércio',   icon: Building2 },
  { id: 'escritorio', label: 'Escritório', icon: Monitor },
  { id: 'industria',  label: 'Indústria',  icon: Factory },
  { id: 'condominio', label: 'Condomínio', icon: Building2 },
];

const WHATSAPP_NUMBER = '5516991378312';

/* ═══════════ Cálculo ══════════════════════════════════════ */
function calcEstimate(sel, cameraQty, alarmZones, wifiCoverage) {
  let min = 0, max = 0;
  const svcs = sel.services.includes('combo') ? ['cameras', 'alarme', 'wifi'] : sel.services;
  if (svcs.includes('cameras') && sel.cameras.qty) {
    const o = cameraQty.find(x => x.id === sel.cameras.qty);
    if (o) { min += o.priceMin; max += o.priceMax; }
    if (sel.cameras.res === '4k') { min += 800; max += 1500; }
  }
  if (svcs.includes('alarme') && sel.alarme.zones) {
    const o = alarmZones.find(x => x.id === sel.alarme.zones);
    if (o) { min += o.priceMin; max += o.priceMax; }
  }
  if (svcs.includes('wifi') && sel.wifi.coverage) {
    const o = wifiCoverage.find(x => x.id === sel.wifi.coverage);
    if (o) { min += o.priceMin; max += o.priceMax; }
  }
  if (sel.services.includes('combo')) { min = Math.round(min * 0.9); max = Math.round(max * 0.9); }
  return { min, max };
}

/* ═══════════ Mensagem WhatsApp ════════════════════════════ */
function buildWA(sel, wifiCoverage) {
  const svcs = sel.services.includes('combo') ? ['cameras', 'alarme', 'wifi'] : sel.services;
  const lines = [
    'Olá! Gostaria de um orçamento para instalação Intelbras:',
    '',
    `🎯 *Serviço:* ${sel.services.map(s => SERVICES.find(x => x.id === s)?.label).join(', ')}`,
  ];
  if (svcs.includes('cameras') && sel.cameras.qty) {
    const r = CAMERA_RES.find(x => x.id === sel.cameras.res);
    lines.push(`📷 *Câmeras:* ${sel.cameras.qty} câmeras${r ? ` ${r.label} ${r.detail}` : ''}`);
  }
  if (svcs.includes('alarme') && sel.alarme.zones)
    lines.push(`🔔 *Alarme:* Central com ${sel.alarme.zones} zonas`);
  if (svcs.includes('wifi') && sel.wifi.coverage) {
    const c = wifiCoverage.find(x => x.id === sel.wifi.coverage);
    lines.push(`📶 *Wi-Fi:* ${c?.label} (~${c?.aps} APs)`);
  }
  if (sel.location.type) {
    const l = LOCATION_TYPES.find(x => x.id === sel.location.type);
    lines.push(`🏢 *Local:* ${l?.label} em ${sel.location.city || 'Ribeirão Preto'}`);
  }
  if (sel.name)  lines.push(`👤 *Nome:* ${sel.name}`);
  if (sel.phone) lines.push(`📱 *Tel:* ${sel.phone}`);
  const { min, max } = calcEstimate(sel, DEFAULT_CAMERA_QTY, DEFAULT_ALARM_ZONES, wifiCoverage);
  if (min > 0) {
    lines.push('');
    lines.push(`💰 *Estimativa:* R$ ${min.toLocaleString('pt-BR')} – R$ ${max.toLocaleString('pt-BR')}`);
  }
  lines.push('');
  lines.push('Poderia confirmar disponibilidade para uma visita técnica gratuita?');
  return encodeURIComponent(lines.join('\n'));
}

/* ═══════════ SelectCard ════════════════════════════════════ */
const SelectCard = ({ selected, onClick, children, badge, sm }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative w-full text-left rounded-xl border-2 transition-all duration-200
      ${sm ? 'p-3' : 'p-4'}
      ${selected
        ? 'border-[#00A651] bg-[#00A651]/10 shadow-md shadow-[#00A651]/10'
        : 'border-slate-700/60 bg-slate-800/40 hover:border-slate-600 hover:bg-slate-800/70'
      }`}
  >
    {badge && (
      <span className="absolute -top-2.5 right-3 text-[10px] font-bold text-white px-2 py-0.5 rounded-full whitespace-nowrap"
        style={{ background: 'linear-gradient(135deg,#00A651,#00c461)' }}>
        {badge}
      </span>
    )}
    {selected && !sm && (
      <CheckCircle2 className="absolute top-3 right-3 w-4 h-4 text-[#00A651]" />
    )}
    {children}
  </button>
);

/* ═══════════ StepIndicator ════════════════════════════════ */
const STEP_LABELS = ['Serviço', 'Detalhes', 'Local', 'Estimativa'];
const StepIndicator = ({ current }) => (
  <nav aria-label="Progresso do orçamento" className="mb-8">
    <ol className="flex items-center justify-center gap-0">
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <li key={n} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all
                  ${active ? 'shadow-md shadow-[#00A651]/30 text-white' : done ? 'text-white' : 'bg-slate-800 border border-slate-700 text-slate-500'}`}
                style={active ? { background: 'linear-gradient(135deg,#00A651,#00c461)' } : done ? { background: '#00A651' } : {}}
              >
                {done ? <CheckCircle2 className="w-4 h-4" /> : n}
              </div>
              <span className={`mt-1 text-[10px] hidden sm:block ${active ? 'text-[#00A651] font-semibold' : done ? 'text-green-600' : 'text-slate-600'}`}>
                {label}
              </span>
            </div>
            {n < 4 && (
              <div className={`w-8 sm:w-14 h-px mx-1 sm:mx-2 mt-0 sm:-mt-4 transition-all ${done ? 'bg-[#00A651]' : 'bg-slate-700'}`} />
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);

/* ═══════════ Breadcrumb visual ════════════════════════════ */
const Breadcrumb = () => (
  <nav aria-label="Você está em" className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap">
    <a href="/" className="hover:text-white transition-colors">Home</a>
    <ChevronRight className="w-3 h-3" />
    <a href="/#intelbras" className="hover:text-white transition-colors">Intelbras</a>
    <ChevronRight className="w-3 h-3" />
    <span className="text-[#00A651] font-medium">Orçamento</span>
  </nav>
);

/* ═══════════════════════════════════════════════════════════
   PÁGINA PRINCIPAL
══════════════════════════════════════════════════════════ */
export default function OrcamentoIntelbras() {
  useSEO();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState({
    services: [],
    cameras: { qty: null, res: 'fullhd' },
    alarme:  { zones: null },
    wifi:    { coverage: null },
    location: { type: null, city: 'Ribeirão Preto' },
    name: '',
    phone: '',
  });

  // ── Preços dinâmicos via API ────────────────────────────────────────
  const [cameraQty, setCameraQty]     = useState(DEFAULT_CAMERA_QTY);
  const [alarmZones, setAlarmZones]   = useState(DEFAULT_ALARM_ZONES);
  const [wifiCoverage, setWifiCoverage] = useState(DEFAULT_WIFI_COVERAGE);
  const [pixDiscountPct, setPixDiscountPct]     = useState(10);
  const [installmentFeePct, setInstallmentFeePct] = useState(18);
  const [maxInstallments, setMaxInstallments]   = useState(12);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/intelbras-pricing`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.cameras?.length)      setCameraQty(data.cameras);
        if (data.alarm_zones?.length)  setAlarmZones(data.alarm_zones);
        if (data.wifi_coverage?.length) setWifiCoverage(data.wifi_coverage);
        if (data.pix_discount_pct  != null) setPixDiscountPct(data.pix_discount_pct);
        if (data.installment_fee_pct != null) setInstallmentFeePct(data.installment_fee_pct);
        if (data.max_installments  != null) setMaxInstallments(data.max_installments);
      })
      .catch(() => {}); // usa defaults em caso de falha
  }, []);

  const update = useCallback((path, val) => {
    setSel(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]] = { ...ref[keys[i]] };
      ref[keys[keys.length - 1]] = val;
      return next;
    });
  }, []);

  const toggleService = (id) => {
    setSel(prev => {
      let svcs = [...prev.services];
      if (id === 'combo') { svcs = svcs.includes('combo') ? [] : ['combo']; }
      else { svcs = svcs.filter(s => s !== 'combo'); svcs = svcs.includes(id) ? svcs.filter(s => s !== id) : [...svcs, id]; }
      return { ...prev, services: svcs };
    });
  };

  const activeSvcs = sel.services.includes('combo') ? ['cameras', 'alarme', 'wifi'] : sel.services;
  const needsCam  = activeSvcs.includes('cameras');
  const needsAlarm = activeSvcs.includes('alarme');
  const needsWifi  = activeSvcs.includes('wifi');
  const step2OK = (!needsCam || sel.cameras.qty) && (!needsAlarm || sel.alarme.zones) && (!needsWifi || sel.wifi.coverage);
  const { min, max } = calcEstimate(sel, cameraQty, alarmZones, wifiCoverage);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const nextStep = (n) => { setStep(n); scrollTop(); };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" style={{ overflowX: 'hidden' }}>
      <Navbar />

      {/* Breadcrumb — fixed logo abaixo da Navbar (mantém visível no scroll) */}
      <StickyBreadcrumb>
        <Breadcrumb />
      </StickyBreadcrumb>

      {/* ── Hero ──────────────────────────────────────── */}
      <header className="relative pt-8 sm:pt-10 xl:pt-12 pb-8 text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90vw] max-w-[600px] h-[250px] bg-[#00A651]/7 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 mb-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#00A651]/10 border border-[#00A651]/30 rounded-full max-w-full">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#00A651] flex-shrink-0" />
            <span className="text-[#00A651] text-xs sm:text-sm font-semibold uppercase tracking-wide truncate">
              Parceiro Autorizado Intelbras
            </span>
          </div>
          {/* H1 */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 leading-tight">
            Orçamento{' '}
            <span className="bg-gradient-to-r from-[#00A651] to-emerald-400 bg-clip-text text-transparent">
              Gratuito
            </span>
          </h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-lg mx-auto leading-relaxed px-2">
            Monte seu kit de segurança em 4 passos e receba uma estimativa de preço instantânea.
          </p>
        </div>
      </header>

      {/* ── Formulário ────────────────────────────────── */}
      <main className="flex-1 w-full mx-auto px-4 pb-16" style={{ maxWidth: '672px' }}>
        <StepIndicator current={step} />

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <section aria-labelledby="step1-title">
            <h2 id="step1-title" className="text-lg sm:text-xl font-bold text-white mb-1">O que você precisa?</h2>
            <p className="text-slate-500 text-sm mb-5">Selecione um ou mais serviços</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
              {SERVICES.map(s => {
                const Icon = s.icon;
                const active = sel.services.includes(s.id);
                return (
                  <SelectCard key={s.id} selected={active} onClick={() => toggleService(s.id)} badge={s.badge} data-testid={`service-${s.id}`}>
                    <div className="flex items-start gap-3 pr-4">
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex-shrink-0 flex items-center justify-center transition-all
                        ${active ? 'text-white' : 'bg-slate-700 text-slate-400'}`}
                        style={active ? { background: 'linear-gradient(135deg,#00A651,#00c461)' } : {}}>
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className={`font-semibold text-sm leading-snug ${active ? 'text-white' : 'text-slate-200'}`}>{s.label}</p>
                        <p className="text-slate-500 text-xs mt-0.5 leading-snug">{s.desc}</p>
                      </div>
                    </div>
                  </SelectCard>
                );
              })}
            </div>
            <Button disabled={sel.services.length === 0} onClick={() => nextStep(2)}
              className="w-full font-bold py-3.5 sm:py-4 text-white disabled:opacity-40 text-sm sm:text-base"
              style={{ background: 'linear-gradient(135deg,#00A651,#00c461)' }}
              data-testid="step1-next">
              Continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </section>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <section aria-labelledby="step2-title">
            <h2 id="step2-title" className="text-lg sm:text-xl font-bold text-white mb-1">Configure seu kit</h2>
            <p className="text-slate-500 text-sm mb-5">Personalize cada serviço selecionado</p>
            <div className="space-y-7">

              {/* Câmeras */}
              {needsCam && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Camera className="w-4 h-4 text-[#00A651] flex-shrink-0" />
                    <h3 className="text-white font-semibold text-sm sm:text-base">Câmeras de Segurança</h3>
                  </div>
                  <p className="text-slate-500 text-xs mb-2">Quantidade de câmeras</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                    {cameraQty.map(o => (
                      <SelectCard key={o.id} selected={sel.cameras.qty === o.id} onClick={() => update('cameras.qty', o.id)}>
                        <p className="text-sm font-semibold text-slate-200">{o.label}</p>
                        <p className="text-slate-500 text-xs mt-0.5">{o.sub}</p>
                      </SelectCard>
                    ))}
                  </div>
                  <p className="text-slate-500 text-xs mb-2">Resolução</p>
                  {/* Resolução: 1 coluna no mobile, 3 no sm+ */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {CAMERA_RES.map(r => (
                      <SelectCard key={r.id} selected={sel.cameras.res === r.id} onClick={() => update('cameras.res', r.id)}>
                        <div className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-0">
                          <div className="flex items-baseline gap-1.5">
                            <p className="text-sm font-bold text-slate-100">{r.label}</p>
                            <span className="text-xs text-slate-400">{r.detail}</span>
                          </div>
                          <p className="text-slate-500 text-xs sm:mt-0.5">{r.sub}</p>
                          {sel.cameras.res === r.id && (
                            <CheckCircle2 className="w-4 h-4 text-[#00A651] ml-auto sm:hidden" />
                          )}
                        </div>
                      </SelectCard>
                    ))}
                  </div>
                </div>
              )}

              {/* Alarme */}
              {needsAlarm && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-[#00A651] flex-shrink-0" />
                    <h3 className="text-white font-semibold text-sm sm:text-base">Sistema de Alarme</h3>
                  </div>
                  <p className="text-slate-500 text-xs mb-2">Número de zonas (pontos de sensor)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {alarmZones.map(z => (
                      <SelectCard key={z.id} selected={sel.alarme.zones === z.id} onClick={() => update('alarme.zones', z.id)}>
                        <p className="text-sm font-semibold text-slate-200">{z.label}</p>
                        <p className="text-slate-500 text-xs mt-0.5">{z.sub}</p>
                      </SelectCard>
                    ))}
                  </div>
                </div>
              )}

              {/* Wi-Fi */}
              {needsWifi && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Wifi className="w-4 h-4 text-[#00A651] flex-shrink-0" />
                    <h3 className="text-white font-semibold text-sm sm:text-base">Rede Wi-Fi Intelbras</h3>
                  </div>
                  <p className="text-slate-500 text-xs mb-2">Área de cobertura necessária</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {wifiCoverage.map(c => (
                      <SelectCard key={c.id} selected={sel.wifi.coverage === c.id} onClick={() => update('wifi.coverage', c.id)}>
                        <p className="text-sm font-semibold text-slate-200">{c.label}</p>
                        <p className="text-slate-500 text-xs mt-0.5">{c.sub} · ~{c.aps} AP{c.aps > 1 ? 's' : ''}</p>
                      </SelectCard>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-7">
              <Button variant="outline" onClick={() => nextStep(1)}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 py-3 sm:py-4 text-sm">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar
              </Button>
              <Button disabled={!step2OK} onClick={() => nextStep(3)}
                className="flex-1 font-bold py-3 sm:py-4 text-white disabled:opacity-40 text-sm"
                style={{ background: 'linear-gradient(135deg,#00A651,#00c461)' }}
                data-testid="step2-next">
                Continuar <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </section>
        )}

        {/* ── STEP 3 ── */}
        {step === 3 && (
          <section aria-labelledby="step3-title">
            <h2 id="step3-title" className="text-lg sm:text-xl font-bold text-white mb-1">Sobre o local</h2>
            <p className="text-slate-500 text-sm mb-5">Informações para personalizar o orçamento</p>
            <div className="space-y-5">

              {/* Tipo de local */}
              <div>
                <p className="text-slate-400 text-sm font-medium mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#00A651] flex-shrink-0" /> Tipo de local
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {LOCATION_TYPES.map(l => {
                    const Icon = l.icon;
                    const active = sel.location.type === l.id;
                    return (
                      <button key={l.id} type="button" onClick={() => update('location.type', l.id)}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all
                          ${active ? 'border-[#00A651] bg-[#00A651]/10' : 'border-slate-700/60 bg-slate-800/40 hover:border-slate-600'}`}>
                        <Icon className={`w-5 h-5 ${active ? 'text-[#00A651]' : 'text-slate-500'}`} />
                        <span className={`text-xs font-medium text-center leading-tight ${active ? 'text-white' : 'text-slate-400'}`}>{l.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Cidade */}
              <div>
                <label className="text-slate-400 text-sm font-medium flex items-center gap-2 mb-1.5">
                  <MapPin className="w-4 h-4 text-[#00A651] flex-shrink-0" /> Cidade
                </label>
                <input type="text" value={sel.location.city} onChange={e => update('location.city', e.target.value)}
                  placeholder="Ex: Ribeirão Preto"
                  className="w-full bg-slate-800/60 border border-slate-700 focus:border-[#00A651]/60 text-white placeholder-slate-500 rounded-xl px-4 py-3 outline-none text-sm transition-colors"
                  data-testid="input-city" />
              </div>

              {/* Nome */}
              <div>
                <label className="text-slate-400 text-sm font-medium flex items-center gap-2 mb-1.5">
                  <User className="w-4 h-4 text-[#00A651] flex-shrink-0" />
                  Seu nome <span className="text-slate-600 text-xs">(opcional)</span>
                </label>
                <input type="text" value={sel.name} onChange={e => setSel(p => ({ ...p, name: e.target.value }))}
                  placeholder="Como quer ser chamado?"
                  className="w-full bg-slate-800/60 border border-slate-700 focus:border-[#00A651]/60 text-white placeholder-slate-500 rounded-xl px-4 py-3 outline-none text-sm transition-colors"
                  data-testid="input-name" />
              </div>

              {/* Telefone */}
              <div>
                <label className="text-slate-400 text-sm font-medium flex items-center gap-2 mb-1.5">
                  <Phone className="w-4 h-4 text-[#00A651] flex-shrink-0" />
                  Telefone <span className="text-slate-600 text-xs">(opcional)</span>
                </label>
                <input type="tel" value={sel.phone} onChange={e => setSel(p => ({ ...p, phone: e.target.value }))}
                  placeholder="(16) 99999-9999"
                  className="w-full bg-slate-800/60 border border-slate-700 focus:border-[#00A651]/60 text-white placeholder-slate-500 rounded-xl px-4 py-3 outline-none text-sm transition-colors"
                  data-testid="input-phone" />
              </div>
            </div>

            <div className="flex gap-3 mt-7">
              <Button variant="outline" onClick={() => nextStep(2)}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 py-3 sm:py-4 text-sm">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar
              </Button>
              <Button onClick={() => nextStep(4)}
                className="flex-1 font-bold py-3 sm:py-4 text-white text-sm"
                style={{ background: 'linear-gradient(135deg,#00A651,#00c461)' }}
                data-testid="step3-next">
                Ver Estimativa <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </section>
        )}

        {/* ── STEP 4 ── */}
        {step === 4 && (
          <section aria-labelledby="step4-title">
            <h2 id="step4-title" className="text-lg sm:text-xl font-bold text-white mb-1">Sua Estimativa</h2>
            <p className="text-slate-500 text-sm mb-5">Resumo do orçamento personalizado</p>

            {/* Caixa de preço — novo formato com PIX + parcelamento */}
            {min > 0 && (() => {
              // Usa o priceMax como base, priceMin como preço PIX
              const pixPrice    = Math.round(max * (1 - pixDiscountPct / 100));
              const installTotal = Math.round(max * (1 + installmentFeePct / 100));
              const installPmt  = installTotal / maxInstallments;
              const fmt = v => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return (
                <div className="rounded-2xl border border-[#00A651]/40 bg-gradient-to-br from-[#00A651]/8 to-slate-900/60 p-5 sm:p-6 mb-5">
                  {/* Badge desconto combo */}
                  {sel.services.includes('combo') && (
                    <div className="flex justify-center mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#00A651] px-3 py-1 rounded-full">
                        <Star className="w-3 h-3" /> 10% OFF Combo Aplicado
                      </span>
                    </div>
                  )}

                  <p className="text-slate-400 text-xs text-center mb-3 uppercase tracking-wider">Estimativa para sua instalação</p>

                  {/* Preço riscado (base) */}
                  <p className="text-center text-slate-500 line-through text-base mb-1">
                    de R$ {fmt(max)}
                  </p>

                  {/* Preço PIX em destaque */}
                  <div className="text-center mb-1">
                    <span className="font-extrabold text-[#00A651] leading-none" style={{ fontSize: 'clamp(2rem,6vw,3rem)' }}>
                      R$ {fmt(pixPrice)}
                    </span>
                  </div>
                  <p className="text-center text-[#00A651] font-semibold text-sm mb-3 flex items-center justify-center gap-1.5">
                    <span className="bg-[#00A651]/15 px-2.5 py-0.5 rounded-full">
                      à vista no PIX ({pixDiscountPct}% OFF)
                    </span>
                  </p>

                  {/* Divisor */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-px flex-1 bg-slate-700/50" />
                    <span className="text-slate-600 text-xs">ou</span>
                    <div className="h-px flex-1 bg-slate-700/50" />
                  </div>

                  {/* Parcelamento */}
                  <p className="text-center text-slate-300 text-sm font-medium">
                    em até <span className="text-white font-bold">{maxInstallments}x</span> de{' '}
                    <span className="text-white font-bold">R$ {fmt(installPmt)}</span> no cartão
                  </p>
                  <p className="text-center text-slate-600 text-[10px] mt-1">
                    Total parcelado: R$ {fmt(installTotal)} · +{installmentFeePct}% taxa da máquina
                  </p>

                  <p className="text-slate-600 text-[10px] mt-4 flex items-center justify-center gap-1 text-center">
                    <Info className="w-3 h-3 flex-shrink-0" />
                    Estimativa sujeita a vistoria técnica gratuita no local
                  </p>
                </div>
              );
            })()}

            {/* Resumo */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 sm:p-5 mb-5 space-y-3">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Resumo do pedido</p>
              <div className="flex items-start gap-3">
                <Package className="w-4 h-4 text-[#00A651] mt-0.5 flex-shrink-0" />
                <div><p className="text-[10px] text-slate-500">Serviços</p>
                  <p className="text-sm text-white font-medium">{sel.services.map(s => SERVICES.find(x => x.id === s)?.label).join(', ')}</p></div>
              </div>
              {needsCam && sel.cameras.qty && (
                <div className="flex items-start gap-3">
                  <Camera className="w-4 h-4 text-[#00A651] mt-0.5 flex-shrink-0" />
                  <div><p className="text-[10px] text-slate-500">Câmeras</p>
                    <p className="text-sm text-white font-medium">{sel.cameras.qty} câmeras · {CAMERA_RES.find(r => r.id === sel.cameras.res)?.label} {CAMERA_RES.find(r => r.id === sel.cameras.res)?.detail}</p></div>
                </div>
              )}
              {needsAlarm && sel.alarme.zones && (
                <div className="flex items-start gap-3">
                  <Shield className="w-4 h-4 text-[#00A651] mt-0.5 flex-shrink-0" />
                  <div><p className="text-[10px] text-slate-500">Alarme</p>
                    <p className="text-sm text-white font-medium">Central com {sel.alarme.zones} zonas</p></div>
                </div>
              )}
              {needsWifi && sel.wifi.coverage && (
                <div className="flex items-start gap-3">
                  <Wifi className="w-4 h-4 text-[#00A651] mt-0.5 flex-shrink-0" />
                  <div><p className="text-[10px] text-slate-500">Wi-Fi</p>
                    <p className="text-sm text-white font-medium">
                      {wifiCoverage.find(c => c.id === sel.wifi.coverage)?.label} · {wifiCoverage.find(c => c.id === sel.wifi.coverage)?.aps} APs
                    </p></div>
                </div>
              )}
              {sel.location.type && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#00A651] mt-0.5 flex-shrink-0" />
                  <div><p className="text-[10px] text-slate-500">Local</p>
                    <p className="text-sm text-white font-medium">{LOCATION_TYPES.find(l => l.id === sel.location.type)?.label} · {sel.location.city}</p></div>
                </div>
              )}
            </div>

            {/* CTA WhatsApp */}
            <button type="button" onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${buildWA(sel, wifiCoverage)}`, '_blank')}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-white font-bold text-sm sm:text-base shadow-xl shadow-[#00A651]/20 transition-all hover:opacity-90 active:scale-[0.98] mb-3"
              style={{ background: 'linear-gradient(135deg,#00A651,#00c461)' }}
              data-testid="send-whatsapp-btn">
              <Phone className="w-5 h-5 flex-shrink-0" />
              Enviar Orçamento pelo WhatsApp
              <ChevronRight className="w-5 h-5 flex-shrink-0" />
            </button>
            <p className="text-center text-slate-600 text-xs mb-5">
              As informações serão enviadas numa mensagem pré-preenchida para nosso especialista.
            </p>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <Button variant="outline" onClick={() => nextStep(3)}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 py-3 text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Editar
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 py-3 text-sm">
                Voltar ao site
              </Button>
            </div>

            {/* Trust badges */}
            <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { icon: '🔒', label: 'Orçamento gratuito' },
                { icon: '⚡', label: 'Resposta em até 2h' },
                { icon: '✅', label: 'Sem compromisso' },
              ].map(t => (
                <div key={t.label} className="bg-slate-900/50 border border-slate-800 rounded-xl py-3 px-1 sm:px-2 text-center">
                  <p className="text-lg sm:text-xl mb-1">{t.icon}</p>
                  <p className="text-[10px] sm:text-xs text-slate-400 font-medium leading-tight">{t.label}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Conteúdo SEO indexável ─────────────────── */}
        <aside className="mt-12 pt-8 border-t border-slate-800/60">
          <h2 className="text-base font-semibold text-slate-300 mb-4">
            Por que escolher a Fernando Tech para instalação Intelbras?
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { dt: 'Parceiro Autorizado Intelbras', dd: 'Técnicos certificados pela Intelbras com experiência em câmeras CFTV, alarmes e redes Wi-Fi.' },
              { dt: 'Atendimento em Ribeirão Preto', dd: 'Instalação rápida em Ribeirão Preto e toda a região de Ribeirão Preto, interior de SP.' },
              { dt: 'Garantia de Fábrica', dd: 'Todos os equipamentos Intelbras instalados possuem garantia de fábrica e suporte pós-instalação.' },
              { dt: 'Acesso Remoto Incluso', dd: 'Configuramos o acesso remoto pelo celular (iSIC, iMDVR) para monitoramento 24h de qualquer lugar.' },
            ].map(item => (
              <div key={item.dt} className="bg-slate-900/40 border border-slate-800/60 rounded-xl p-4">
                <dt className="text-sm font-semibold text-white mb-1">{item.dt}</dt>
                <dd className="text-xs text-slate-500 leading-relaxed">{item.dd}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </main>

      <Footer onOpenPrivacyPolicy={() => {}} onOpenAdmin={() => {}} />
    </div>
  );
}
